import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

// Score Overlay Component for OBS Browser Source
// Usage: Add as Browser Source in OBS with URL: /overlay/{matchId}
// Optional params: ?theme=minimal|detailed|ipl&position=top|bottom

export default function ScoreOverlay() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = searchParams.get('theme') || 'detailed';
  const position = searchParams.get('position') || 'bottom';

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const response = await axios.get(`${API}/matches/${matchId}`);
        setMatch(response.data);
        setError(null);
      } catch (err) {
        setError('Match not found');
        console.error('Error fetching match:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
    
    // Poll for updates every 3 seconds for real-time score updates
    const interval = setInterval(fetchMatch, 3000);
    return () => clearInterval(interval);
  }, [matchId]);

  if (loading) {
    return <div className="overlay-container" style={{ background: 'transparent' }} />;
  }

  if (error || !match) {
    return (
      <div className="overlay-container" style={{ background: 'transparent' }}>
        <div className="overlay-error">Match not found</div>
      </div>
    );
  }

  const currentInnings = match.innings?.[match.current_innings - 1];
  const battingTeamName = currentInnings?.batting_team_id === match.team1_id 
    ? match.team1_name 
    : match.team2_name;
  const bowlingTeamName = currentInnings?.batting_team_id === match.team1_id 
    ? match.team2_name 
    : match.team1_name;

  const runs = currentInnings?.total_runs || 0;
  const wickets = currentInnings?.wickets || 0;
  const overs = currentInnings?.overs_completed || 0;
  const balls = currentInnings?.balls_in_current_over || 0;
  const target = match.current_innings === 2 ? (match.innings?.[0]?.total_runs || 0) + 1 : null;
  
  // Calculate run rate
  const totalBalls = overs * 6 + balls;
  const runRate = totalBalls > 0 ? ((runs / totalBalls) * 6).toFixed(2) : '0.00';
  
  // Required run rate for second innings
  let requiredRate = null;
  if (target && match.current_innings === 2) {
    const remainingRuns = target - runs;
    const remainingBalls = (match.overs * 6) - totalBalls;
    if (remainingBalls > 0) {
      requiredRate = ((remainingRuns / remainingBalls) * 6).toFixed(2);
    }
  }

  // Get current batsmen
  const striker = currentInnings?.batsmen?.find(b => b.is_striker);
  const nonStriker = currentInnings?.batsmen?.find(b => b.status === 'batting' && !b.is_striker);
  const currentBowler = currentInnings?.current_bowler_id 
    ? currentInnings?.bowlers?.find(b => b.player_id === currentInnings.current_bowler_id)
    : null;

  // Render based on theme
  if (theme === 'minimal') {
    return (
      <MinimalOverlay 
        match={match}
        battingTeamName={battingTeamName}
        runs={runs}
        wickets={wickets}
        overs={overs}
        balls={balls}
        position={position}
      />
    );
  }

  if (theme === 'ipl') {
    return (
      <IPLOverlay 
        match={match}
        battingTeamName={battingTeamName}
        bowlingTeamName={bowlingTeamName}
        runs={runs}
        wickets={wickets}
        overs={overs}
        balls={balls}
        target={target}
        runRate={runRate}
        requiredRate={requiredRate}
        striker={striker}
        nonStriker={nonStriker}
        currentBowler={currentBowler}
        position={position}
      />
    );
  }

  // Default: Detailed theme
  return (
    <DetailedOverlay 
      match={match}
      battingTeamName={battingTeamName}
      bowlingTeamName={bowlingTeamName}
      runs={runs}
      wickets={wickets}
      overs={overs}
      balls={balls}
      target={target}
      runRate={runRate}
      requiredRate={requiredRate}
      striker={striker}
      nonStriker={nonStriker}
      currentBowler={currentBowler}
      position={position}
    />
  );
}

// Minimal Overlay - Just score and overs
function MinimalOverlay({ battingTeamName, runs, wickets, overs, balls, position }) {
  return (
    <div className={`fixed ${position === 'top' ? 'top-4' : 'bottom-4'} left-4 z-50`}>
      <div className="bg-black/80 backdrop-blur-sm rounded-lg px-4 py-2 text-white shadow-2xl border border-white/10">
        <div className="flex items-center gap-3">
          <span className="font-bold text-lg">{battingTeamName}</span>
          <span className="text-2xl font-bold text-yellow-400">{runs}/{wickets}</span>
          <span className="text-sm text-gray-300">({overs}.{balls})</span>
        </div>
      </div>
    </div>
  );
}

// Detailed Overlay - Score, batsmen, bowler, rates
function DetailedOverlay({ 
  match, battingTeamName, bowlingTeamName, runs, wickets, overs, balls, 
  target, runRate, requiredRate, striker, nonStriker, currentBowler, position 
}) {
  return (
    <div className={`fixed ${position === 'top' ? 'top-4' : 'bottom-4'} left-4 right-4 z-50`}>
      <div className="bg-gradient-to-r from-gray-900/95 to-gray-800/95 backdrop-blur-sm rounded-xl overflow-hidden shadow-2xl border border-white/10 max-w-2xl">
        {/* Main Score Bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-teal-700 to-teal-600">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">Live</span>
            </div>
            <span className="font-bold text-white text-lg">{battingTeamName}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-3xl font-bold text-white">{runs}<span className="text-white/70">/{wickets}</span></span>
            <div className="text-right">
              <div className="text-white font-semibold">{overs}.{balls} ov</div>
              <div className="text-xs text-white/70">RR: {runRate}</div>
            </div>
          </div>
        </div>

        {/* Target info for 2nd innings */}
        {target && (
          <div className="bg-yellow-500/90 px-4 py-1 text-center">
            <span className="text-sm font-semibold text-black">
              Target: {target} • Need {target - runs} from {(match.overs * 6) - (overs * 6 + balls)} balls
              {requiredRate && <span> • RRR: {requiredRate}</span>}
            </span>
          </div>
        )}

        {/* Batsmen & Bowler Info */}
        <div className="px-4 py-2 flex items-center justify-between text-white text-sm">
          <div className="flex items-center gap-4">
            {striker && (
              <div className="flex items-center gap-2">
                <span className="text-yellow-400">●</span>
                <span className="font-medium">{striker.player_name}</span>
                <span className="text-white/70">{striker.runs}({striker.balls})</span>
              </div>
            )}
            {nonStriker && (
              <div className="flex items-center gap-2">
                <span className="text-white/50">○</span>
                <span className="font-medium">{nonStriker.player_name}</span>
                <span className="text-white/70">{nonStriker.runs}({nonStriker.balls})</span>
              </div>
            )}
          </div>
          {currentBowler && (
            <div className="flex items-center gap-2 text-right">
              <span className="text-white/50">🎳</span>
              <span className="font-medium">{currentBowler.player_name}</span>
              <span className="text-white/70">{currentBowler.wickets}/{currentBowler.runs}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// IPL-Style Overlay - Colorful with team branding
function IPLOverlay({ 
  match, battingTeamName, bowlingTeamName, runs, wickets, overs, balls, 
  target, runRate, requiredRate, striker, nonStriker, currentBowler, position 
}) {
  // Team colors (could be customized per team in future)
  const teamColor = 'from-blue-600 to-blue-800';
  
  return (
    <div className={`fixed ${position === 'top' ? 'top-4' : 'bottom-4'} left-4 z-50`}>
      <div className="relative">
        {/* Main Score Box */}
        <div className={`bg-gradient-to-r ${teamColor} rounded-lg overflow-hidden shadow-2xl min-w-[320px]`}>
          {/* Live Badge */}
          <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
            LIVE
          </div>

          {/* Team & Score */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-xl">🏏</span>
                </div>
                <div>
                  <div className="font-bold text-white text-lg">{battingTeamName}</div>
                  <div className="text-white/70 text-xs">vs {bowlingTeamName}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-white">
                  {runs}<span className="text-white/60 text-2xl">/{wickets}</span>
                </div>
                <div className="text-white/80 text-sm font-medium">{overs}.{balls} overs</div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="bg-black/30 px-4 py-2 flex items-center justify-between text-white text-xs">
            <div className="flex items-center gap-4">
              <span>CRR: <span className="font-bold text-yellow-300">{runRate}</span></span>
              {requiredRate && (
                <span>RRR: <span className="font-bold text-red-300">{requiredRate}</span></span>
              )}
            </div>
            {target && (
              <span className="text-yellow-300 font-semibold">Target: {target}</span>
            )}
          </div>

          {/* Current Players */}
          <div className="bg-black/50 px-4 py-2">
            <div className="flex items-center justify-between text-white text-xs">
              <div className="flex items-center gap-3">
                {striker && (
                  <span>
                    <span className="text-yellow-400 font-bold">★</span> {striker.player_name?.split(' ').pop()} {striker.runs}({striker.balls})
                  </span>
                )}
                {nonStriker && (
                  <span className="text-white/60">
                    {nonStriker.player_name?.split(' ').pop()} {nonStriker.runs}({nonStriker.balls})
                  </span>
                )}
              </div>
              {currentBowler && (
                <span className="text-cyan-300">
                  {currentBowler.player_name?.split(' ').pop()} {currentBowler.wickets}-{currentBowler.runs}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
